import Vue from 'vue';
import Router from 'vue-router';

import { PATHS, PERMISSIONS, CALL_STATUSES } from './consts';
import { scrollToTop } from './helpers';
import store from './store';
import { hasPermissions } from './helpers/permission';

import Home from './views/Home';
import Login from './views/Login';
import UserPassword from './views/UserPassword';
import SearchResult from './views/SearchResult';
import PaymentReminder, { TABS as PAYMENT_REMINDER_TABS } from './views/PaymentReminder';
import ContractList from './views/ContractList';
import MyOrder from './views/Order/MyOrder';
import OrderManagement from './views/Order';
import ContractDetail from './views/ContractDetail';
import ContractRedeem from './views/ContractRedeem';
import ContractRepayment from './views/ContractRepayment';
import ContractReprint from './views/ContractReprint';
import ValueAdjustmentList from './views/ValueAdjustmentList';
import CashLog, { TABS as CASH_LOG_TABS } from './views/CashLog';
import ContractNew from './views/ContractNew';
import ClientList from './views/ClientList';
import ClientDetail from './views/ClientDetail';
import AssetsManagement from './views/Assets';

import ContractEditor from './views/ContractEditor';

Vue.use(Router);

const routes = [
  { path: '*', redirect: PATHS.HOME },
  {
    path: PATHS.LOGIN,
    component: Login,
    name: 'login',
    meta: {
      requiresUnauth: true,
      headless: true,
      footless: true
    },
  },
  {
    path: PATHS.USER_PASSWORD,
    component: UserPassword,
    name: 'user-password',
    meta: {
      requiresAuth: true
    },
  },
  {
    path: PATHS.HOME,
    component: Home,
    name: 'home',
    meta: {
      requiresAuth: true
    },
    beforeEnter(to, from, next) {
      if (process.env.VUE_APP_DEBUG) {
        next();
      } else {
        next({ path: PATHS.ORDERS });
      }
    }
  },
  {
    path: PATHS.SEARCH,
    component: SearchResult,
    name: 'SearchResult',
    meta: {
      requiresAuth: true
    }
  },
  {
    path: PATHS.PAYMENT_REMINDER,
    component: PaymentReminder,
    name: 'payment-reminder',
    meta: {
      requiresAuth: true,
      permissions: [PERMISSIONS.PAYMENT_REMINDER_ACCESS]
    },
    children: [
      {
        path: PATHS.PAYMENT_REMINDER_REPORTS,
        name: 'payment-reminder-reports',
        meta: {
          module: PAYMENT_REMINDER_TABS.REPORTS
        }
      }
    ]
  },
  {
    path: PATHS.CONTRACTS,
    component: ContractList,
    name: 'contracts',
    meta: {
      requiresAuth: true,
      permissions: [PERMISSIONS.CONTRACT_ACCESS]
    }
  },
  {
    path: PATHS.CONTRACT_EDITOR,
    component: ContractEditor,
    name: 'contract-editor',
    meta: {
      requiresAuth: true
    }
  },
  {
    path: PATHS.CONTRACT_REVIEW,
    component: ContractEditor,
    name: 'contract-review',
    meta: {
      requiresAuth: true
    },
    props: {
      review: true
    }
  },
  {
    path: PATHS.CONTRACT_NEW,
    component: ContractNew,
    name: 'new-contracts',
    meta: {
      requiresAuth: true
    }
  },
  {
    path: PATHS.CONTRACT_DETAIL,
    name: 'contract-detail',
    component: ContractDetail,
    meta: {
      requiresAuth: true
    },
    props: (route) => ({ id: route.params.id })
  },
  {
    path: PATHS.CONTRACT_REPRINT,
    component: ContractReprint,
    name: 'contract-reprint',
    meta: {
      requiresAuth: true
    }
  },
  {
    path: PATHS.CONTRACT_REDEEM,
    name: 'contract-redeem',
    component: ContractRedeem,
    meta: {
      requiresAuth: true
    }
  },

  // v2
  {
    path: PATHS.CONTRACT_REPAYMENT,
    name: 'contract-repayment',
    component: ContractRepayment,
    meta: {
      requiresAuth: true
    }
  },
  {
    path: PATHS.CONTRACT_LIQUIDIZATION,
    name: 'contract-liquidization',
    component: ContractRepayment,
    meta: {
      requiresAuth: true
    },
    props: () => ({ liquidization: true })
  },
  // v2
  {
    path: PATHS.CONTRACT_RENEWAL,
    name: 'contract-renewal',
    component: ContractEditor,
    meta: {
      requiresAuth: true
    }
  },

  {
    path: PATHS.ORDERS,
    component: OrderManagement,
    name: 'orders',
    meta: {
      requiresAuth: true,
      permissions: [PERMISSIONS.ORDER_ACCESS]
    }
  },
  {
    path: PATHS.MY_ORDERS,
    component: MyOrder,
    name: 'my-orders',
    meta: {
      requiresAuth: true,
      permissions: [PERMISSIONS.ORDER_ACCESS]
    }
  },
  {
    path: PATHS.ORDER_DETAIL,
    component: OrderManagement,
    name: 'order-detail',
    meta: {
      requiresAuth: true
    },
    beforeEnter(to, from, next) {
      if (process.env.VUE_APP_DEBUG) {
        next();
      } else {
        let target = '_self';
        if (store.state.phone.status === CALL_STATUSES.TALKING) {
          target = '_blank';
        }

        window.open(`${process.env.VUE_APP_SITE_URL}/orders/${to?.params?.id}`, target);
      }
    }
  },
  {
    path: PATHS.VALUE_ADJUSTMENT,
    component: ValueAdjustmentList,
    name: 'value-adjustment',
    meta: {
      requiresAuth: true,
      permissions: [PERMISSIONS.VALUE_ADJUSTMENT_ACCESS]
    }
  },
  {
    path: PATHS.CLIENTS,
    component: ClientList,
    name: 'clients',
    meta: {
      requiresAuth: true,
      permissions: [PERMISSIONS.CLIENT_ACCESS]
    }
  },
  {
    path: PATHS.CLIENT_DETAIL,
    name: 'client-detail',
    component: ClientDetail,
    meta: {
      requiresAuth: true
    }
  },

  {
    path: PATHS.CASH_LOG,
    component: CashLog,
    name: 'cash-log',
    meta: {
      requiresAuth: true,
      permissions: [PERMISSIONS.CASH_LOG_ACCESS]
    },
    children: [
      {
        path: PATHS.CASH_LOG_PENDING_LIST,
        name: 'cash-log-pending-list',
        meta: {
          requiresAuth: true,
          module: CASH_LOG_TABS.PENDING_LIST
        }
      },
      {
        path: PATHS.CASH_LOG_CLOSURE,
        name: 'cash-log-closure',
        meta: {
          requiresAuth: true,
          module: CASH_LOG_TABS.CLOSURE
        }
      }
    ]
  },
  {
    path: PATHS.ASSETS,
    name: 'assets',
    component: AssetsManagement,
    meta: {
      requiresAuth: true,
      permissions: [PERMISSIONS.ASSET_ACCESS]
    }
  }
];

const router = new Router({
  mode: 'history',
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (to.name !== from.name) {
      scrollToTop();
    }

    return savedPosition;
  }
});

router.beforeEach((to, from, next) => {
  if (to.matched.some((record) => record.meta.requiresAuth)
  && !store.getters['user/isLoggedIn']) {
    store.dispatch('user/logout');
    next({ path: PATHS.LOGIN });
  } else {
    next();
  }
});

router.beforeEach((to, from, next) => {
  if (to.matched.some((record) => record.meta.requiresUnauth)
  && store.getters['user/isLoggedIn']) {
    next({ path: PATHS.HOME });
  } else {
    next();
  }
});

router.beforeEach((to, from, next) => {
  const matched = to.matched.filter((record) => !!record.meta.permissions?.length);

  if (matched.length && !matched.every((record) => hasPermissions(record.meta.permissions))) {
    next({ path: PATHS.HOME });
  } else {
    next();
  }
});

export default router;
